import { AuthService } from '@/services/AuthService';
import {
  IVendorOrderItem,
  IVendorShort,
  VendorOrderOfferStatus
} from '@/types/IVendorOrderResponse';
import { api } from '@/utils/api';
import {
  CustomError,
  getErrorData,
  IGenericResponseRoot
} from '@/utils/getErrorData';
import { getHeaders } from '@/utils/getHeaders';
import axios from 'axios';
import { toast } from 'react-toastify';

export interface IVendorOrderCreateResponse {
  id: string;
  vendor: IVendorShort;
  created_at: string;
  updated_at: string | null;
  status: VendorOrderOfferStatus;
  vendorOrderOffers: IVendorOrderItem[];
}

export interface ICreateVendorOrderParams {
  vendor_id: string;
  signal?: AbortSignal;
}

export const createVendorOrder = async (
  props: ICreateVendorOrderParams
): Promise<IGenericResponseRoot<IVendorOrderCreateResponse> | 'Abort'> => {
  const { vendor_id, signal } = props;

  try {
    const headers: Record<string, string> = getHeaders();

    if (AuthService.authToken !== null) {
      headers.Authorization = `Bearer ${AuthService.authToken}`;
    } else {
      toast('Пользователь не авторизирован', { type: 'error' });
      throw new Error('Пользователь не авторизирован');
    }

    const response = await api.post<
      IGenericResponseRoot<IVendorOrderCreateResponse>
    >(
      `/admin/vendor-order/order/${vendor_id}`,
      {},
      {
        headers,
        signal
      }
    );

    if (response.data?.meta?.error !== null) {
      const extracted = getErrorData(response.data);
      throw new CustomError(extracted.message, extracted?.code);
    }

    if (response.status < 200 || response.status >= 300) {
      throw new CustomError(
        'Не удалось создать заявку для поставщика',
        response?.status
      );
    }

    return response.data;
  } catch (error: unknown) {
    if (axios.isCancel(error)) {
      return 'Abort';
    }
    // eslint-disable-next-line no-console
    console.error('Ошибка при создании заявки для поставщика:', error);
    throw error;
  }
};

export interface IGetVendorOrdersParams {
  limit?: number;
  offset?: number;
  vendor_id?: string | number;
  status?: string;
  signal?: AbortSignal;
}

export interface IVendorOrderListItem {
  id: string;
  vendor: IVendorShort;
  created_at: string;
  updated_at: string | null;
  status: VendorOrderOfferStatus;
}

export interface IVendorOrdersResponse {
  items: IVendorOrderListItem[];
  meta: {
    total: number;
  };
}

export const getVendorOrders = async (
  props: IGetVendorOrdersParams
): Promise<IGenericResponseRoot<IVendorOrdersResponse> | 'Abort'> => {
  const { limit, offset, vendor_id, status, signal } = props;

  try {
    const headers: Record<string, string> = getHeaders();

    if (AuthService.authToken !== null) {
      headers.Authorization = `Bearer ${AuthService.authToken}`;
    } else {
      toast('Пользователь не авторизирован', { type: 'error' });
      throw new Error('Пользователь не авторизирован');
    }

    const params: Record<string, string | number> = {};
    if (limit !== undefined) params.limit = limit;
    if (offset !== undefined) params.offset = offset;
    if (vendor_id !== undefined) params.vendor_id = vendor_id;
    if (status !== undefined) params.status = status;

    const response = await api.get<IGenericResponseRoot<IVendorOrdersResponse>>(
      '/admin/vendor-order/orders',
      {
        headers,
        params,
        signal
      }
    );

    if (response.data?.meta?.error !== null) {
      const extracted = getErrorData(response.data);
      throw new CustomError(extracted.message, extracted?.code);
    }

    if (response.status < 200 || response.status >= 300) {
      throw new CustomError(
        'Не удалось получить список заказов поставщика',
        response?.status
      );
    }

    return response.data;
  } catch (error: unknown) {
    if (axios.isCancel(error)) {
      return 'Abort';
    }
    // eslint-disable-next-line no-console
    console.error('Ошибка при получении заказов поставщика:', error);
    throw error;
  }
};

export interface IGetVendorOrderParams {
  order_id: string;
  signal?: AbortSignal;
}

export const getVendorOrder = async (
  props: IGetVendorOrderParams
): Promise<IGenericResponseRoot<IVendorOrderCreateResponse> | 'Abort'> => {
  const { order_id, signal } = props;

  try {
    const headers: Record<string, string> = getHeaders();

    if (AuthService.authToken !== null) {
      headers.Authorization = `Bearer ${AuthService.authToken}`;
    } else {
      toast('Пользователь не авторизирован', { type: 'error' });
      throw new Error('Пользователь не авторизирован');
    }

    const response = await api.get<
      IGenericResponseRoot<IVendorOrderCreateResponse>
    >(`/admin/vendor-order/order/${order_id}`, {
      headers,
      signal
    });

    if (response.data?.meta?.error !== null) {
      const extracted = getErrorData(response.data);
      throw new CustomError(extracted.message, extracted?.code);
    }

    if (response.status < 200 || response.status >= 300) {
      throw new CustomError(
        'Не удалось получить данные заявки поставщика',
        response?.status
      );
    }

    return response.data;
  } catch (error: unknown) {
    if (axios.isCancel(error)) {
      return 'Abort';
    }
    // eslint-disable-next-line no-console
    console.error('Ошибка при получении данных заявки поставщика:', error);
    throw error;
  }
};

export interface IDownloadVendorOrderParams {
  order_uuid: string;
  signal?: AbortSignal;
}

export const downloadVendorOrder = async (
  props: IDownloadVendorOrderParams
): Promise<IGenericResponseRoot<string> | 'Abort'> => {
  const { order_uuid, signal } = props;

  try {
    const headers: Record<string, string> = getHeaders();

    if (AuthService.authToken !== null) {
      headers.Authorization = `Bearer ${AuthService.authToken}`;
    } else {
      toast('Пользователь не авторизирован', { type: 'error' });
      throw new Error('Пользователь не авторизирован');
    }

    const response = await api.get<IGenericResponseRoot<string>>(
      `/admin/vendor-order/order/${order_uuid}/download`,
      {
        headers,
        signal
      }
    );

    if (response.data?.meta?.error !== null) {
      const extracted = getErrorData(response.data);
      throw new CustomError(extracted.message, extracted?.code);
    }

    if (response.status < 200 || response.status >= 300) {
      throw new CustomError(
        'Не удалось получить ссылку на скачивание документов заявки поставщика',
        response?.status
      );
    }

    return response.data;
  } catch (error: unknown) {
    if (axios.isCancel(error)) {
      return 'Abort';
    }
    // eslint-disable-next-line no-console
    console.error(
      'Ошибка при получении ссылки на скачивание документов заявки поставщика:',
      error
    );
    throw error;
  }
};
